import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Divider from '@amzn/meridian/divider';
import React, { useMemo } from 'react';
import {
  type AssetDto,
  type CampaignDesiredStateDto,
} from 'src/store/generated/leela-api-generated';
import { useTranslation } from 'src/arb-wrapper/useTranslation';
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import { AssetDisplay } from './AssetCard';
import { CampaignIdList } from './AssetTable';
import { extractFilenameFromAsset } from 'src/utils/AssetUtils';
import { formatUtcDate, formatUtcDateTimeWithZone } from 'src/utils/formatDateTime';
import { buildStrategyLabelMap, resolveStrategyLabel } from 'src/utils/strategyLabels';
import { AssetSourceTag } from './AssetSourceTag';

import { RELEASE_DATE_FALLBACK, ReleaseDateSkeleton } from './release-date-display';

type TitleReleaseDate = NonNullable<AssetDetailViewProps['titleReleaseDates']>[number];

function getReleaseDateDisplayValues (
  titleReleaseDates: readonly TitleReleaseDate[] | undefined,
): string[] {
  if (!titleReleaseDates?.length) return [RELEASE_DATE_FALLBACK];
  if (titleReleaseDates.length === 1) {
    return [formatUtcDate(titleReleaseDates[0].releaseDate, RELEASE_DATE_FALLBACK)];
  }
  return titleReleaseDates.map(({ name, releaseDate }) =>
    `${name ?? RELEASE_DATE_FALLBACK} — ${formatUtcDate(releaseDate, RELEASE_DATE_FALLBACK)}`,
  );
}

export interface AssetDetailViewProps {
  asset: AssetDto;
  campaigns: CampaignDesiredStateDto[];
  isTitleReleaseDatesLoading?: boolean;
  programId: string;
  titleReleaseDates?: ReadonlyArray<{ name?: string; releaseDate?: string }>;
}

export const AssetDetailView = ({
  asset,
  campaigns,
  isTitleReleaseDatesLoading,
  programId,
  titleReleaseDates,
}: AssetDetailViewProps) => {
  const { assetId, assetType, campaignIds, locale } = asset;
  const { t } = useTranslation();
  const isReleaseDateEnabled = isFeatureEnabled(
    FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  );
  const releaseDateDisplayValues = getReleaseDateDisplayValues(titleReleaseDates);

  // Show the row's own strategy (by name); strategy-less rows fall back to the campaigns' strategy ids.
  const strategies = useMemo(() => {
    if (asset.productStrategyId) {
      return [resolveStrategyLabel(buildStrategyLabelMap(campaigns), asset.productStrategyId)];
    }
    const _strategies = new Set<string>();
    campaigns.forEach((campaign) => {
      if (!campaignIds.includes(campaign.campaignId)) {
        return false;
      }
      campaign.productStrategies.forEach((strategy) => {
        if (strategy.strategyId) {
          _strategies.add(strategy.strategyId);
        }
        if (strategy.pageId) {
          _strategies.add(strategy.pageId);
        }
      });
    });
    return Array.from(_strategies);
  }, [asset.productStrategyId, campaigns, campaignIds]);

  const assetFilename = useMemo(() => extractFilenameFromAsset(asset, t), [asset, t]);

  return (
    <div style={{ width: '100%', margin: '0 auto' }}>
      <Box spacingInset="500" type="outline">
        <Row
          alignmentHorizontal="justify"
          alignmentVertical="stretch"
          spacing="500"
          width="100%"
          widths="fill"
        >
            <Column spacingInset="400">
              <Box>
                <Text truncate={true} type="h300">
                  {t('asset_detail_asset_preview_title')}
                </Text>
                <Text color="secondary" truncate={true} type="b300">
                  {t('asset_detail_preview_sub_title', { assetId })}
                </Text>
              </Box>
              <Divider />
                <AssetDisplay asset={asset} programId={programId}/>
            </Column>
            <Column spacing="600">
              <Column spacing="300">
                <Text type="h200">
                  {t('asset_detail_general_title')}
                </Text>
                <Divider />
                <Row>
                  <Column alignmentHorizontal="start" spacing="100">
                    <Text color="secondary" type="b300">
                      {t('asset_detail_view_locale')}
                    </Text>
                    <Text type="b300">{locale}</Text>
                  </Column>
                  <Column alignmentHorizontal="start" spacing="100">
                    <Text color="secondary" type="b300">
                      {t('asset_detail_view_type')}
                    </Text>
                    <Text type="b300">{assetType}</Text>
                  </Column>
                  <AssetSourceTag
                    assetId={assetId}
                    generationMethods={asset.metadata?.generationMethods}
                    releaseDates={titleReleaseDates?.map(({ releaseDate }) => releaseDate)}
                  />
                  {isReleaseDateEnabled && (
                    <Column
                      alignmentHorizontal="start"
                      data-testid="asset-detail-release-date"
                      spacing="100"
                    >
                      <Text color="secondary" type="b300">
                        {t('asset_detail_view_release_date')}
                      </Text>
                      {isTitleReleaseDatesLoading
                        ? <ReleaseDateSkeleton testId="asset-detail-release-date-skeleton" />
                        : releaseDateDisplayValues.map((releaseDate, index) => (
                          <Text
                            data-testid="asset-detail-release-date-value"
                            key={`${releaseDate}-${index}`}
                            type="b300"
                          >
                            {releaseDate}
                          </Text>
                        ))}
                    </Column>
                  )}
                </Row>
              </Column>
              <AssetDetailStrategiesView strategies={strategies} />
              <AssetDetailCampaignsView campaignIds={campaignIds} campaigns={campaigns}/>
              <Column spacing="300">
                <Text type="h200">
                  {t('asset_detail_filename_title')}
                </Text>
                <Divider />
                <Row>
                  <Text type="b300">{assetFilename}</Text>
                </Row>
              </Column>
              {asset.schedule && (
                <Column spacing="300">
                  <Text type="h200">{t('asset_detail_schedule_title')}</Text>
                  <Divider />
                  <Row spacing="500">
                    <Column alignmentHorizontal="start" spacing="100">
                      <Text color="secondary" type="b300">{t('asset_schedule_goes_live_label')}</Text>
                      <Text data-testid="asset-detail-schedule-start" type="b300">
                        {formatUtcDateTimeWithZone(
                          asset.schedule.startTime,
                          'Invalid DateTime UTC',
                        )}
                      </Text>
                    </Column>
                    <Column alignmentHorizontal="start" spacing="100">
                      <Text color="secondary" type="b300">{t('asset_schedule_taken_down_label')}</Text>
                      <Text data-testid="asset-detail-schedule-end" type="b300">
                        {formatUtcDateTimeWithZone(
                          asset.schedule.endTime,
                          'Invalid DateTime UTC',
                        )}
                      </Text>
                    </Column>
                  </Row>
                </Column>
              )}
            </Column>
          </Row>
        </Box>
      </div>
  );
};

interface AssetDetailStrategiesViewProps {
  strategies: string[];
}

export const AssetDetailStrategiesView = (
  props: AssetDetailStrategiesViewProps,
) => {
  const { t } = useTranslation();
  const { strategies } = props;

  return (
    <Column spacing="300">
      <Row>
        <Text type="h200">{t('asset_detail_strategies_title')}</Text>
        <Text color="secondary" type="h200">
          ({strategies.length})
        </Text>
      </Row>
      <Divider />
      {strategies.map((strategy) => {
        return (
          <Text key={strategy} type="b300">{strategy}</Text>
        );
      })}
    </Column>
  );
};

interface AssetDetailCampaignsViewProps {
  campaignIds: string[];
  campaigns: CampaignDesiredStateDto[];
}

export const AssetDetailCampaignsView = (
  props: AssetDetailCampaignsViewProps,
) => {
  const { t } = useTranslation();
  const { campaignIds, campaigns } = props;

  return (
    <Column spacing="300">
      <Row>
        <Text type="h200">{t('asset_detail_campaign_ids_title')}</Text>
        <Text color="secondary" type="h200">
          ({campaignIds.length})
        </Text>
      </Row>
      <Divider />
      <CampaignIdList campaignIds={campaignIds} campaigns={campaigns} />
    </Column>
  );
};
